import moment from 'moment';
import { ForgeViewer } from '../forge-viewer';

type InputScheduleData = { earlyStartDate: Date; earlyEndDate: Date; activityCode: string; activityName: string };
type ScheduleMap = Map<string, { startDate: Date; endDate: Date; activityCode: string }>;
type ActivityDbIdsMap = Map<string, number[]>;

export class SchedulePlaningTool implements Autodesk.Viewing.ToolInterface {
    private name = 'schedule-planning-tool';

    private viewer: Autodesk.Viewing.GuiViewer3D | null = null;
    private forgeViewer: ForgeViewer;
    private scheduleMap: ScheduleMap;

    constructor(schedulingData: InputScheduleData[]) {
        this.scheduleMap = this.createScheduleMap(schedulingData);
    }

    getForgeViewer(): ForgeViewer | null {
        return this.forgeViewer || null;
    }

    // Custom function to register and activate the tool at the same time
    attach(forgeViewer: ForgeViewer) {
        this.forgeViewer = forgeViewer;

        forgeViewer.instance.toolController.registerTool(this);
        forgeViewer.instance.toolController.activateTool(this.name);
    }

    getName(): string {
        return this.name;
    }

    getNames(): string[] {
        return [this.name];
    }

    getPriority?(): number {
        return 50;
    }

    register(): void {}

    deregister(): void {}

    activate(name: string, viewer?: Autodesk.Viewing.GuiViewer3D): void {
        this.viewer = viewer;
    }

    deactivate(name: string): void {}

    update(highResTimestamp?: number): boolean {
        return false;
    }

    private createScheduleMap(data: InputScheduleData[]): ScheduleMap {
        return data.reduce((acc, cur) => {
            acc.set(cur.activityCode, {
                startDate: moment(cur.earlyStartDate, 'YYYYMMDD').toDate(),
                endDate: moment(cur.earlyEndDate, 'YYYYMMDD').toDate(),
                activityCode: cur.activityCode,
            });
            return acc;
        }, new Map() as ScheduleMap);
    }

    calculateDateRange() {
        if (this.scheduleMap.size === 0) return null;

        let min = new Date(8640000000000000); // Max date
        let max = new Date(-8640000000000000); // Min date

        this.scheduleMap.forEach((schedule) => {
            if (schedule.startDate < min) min = schedule.startDate;
            if (schedule.endDate > max) max = schedule.endDate;
        });

        return { min, max };
    }

    async getActivityDbIdsMap(propName: string = 'ACTIVITY ID'): Promise<Map<string, number[]>> {
        return new Promise<Map<string, number[]>>((resolve) => {
            if (!this.viewer.model) {
                console.error('Model not loaded');
                resolve(new Map());
                return;
            }

            this.viewer.model.getBulkProperties([], {}, (results) => {
                const activityMap = new Map<string, number[]>();

                results.forEach((item) => {
                    const activityProp = item.properties.find((p) => p.displayName === propName);

                    if (activityProp?.displayValue) {
                        const activityId = String(activityProp.displayValue);
                        const existing = activityMap.get(activityId) || [];
                        existing.push(item.dbId);
                        activityMap.set(activityId, existing);
                    }
                });

                resolve(activityMap);
            });
        });
    }

    updateVisibility(date: Date, activityDbIdsMap: ActivityDbIdsMap) {
        const visibleDbIds: number[] = [];
        const hiddenDbIds: number[] = [];

        // Determine visibility based on schedule
        this.scheduleMap.forEach((schedule, taskCode) => {
            const dbIds = activityDbIdsMap.get(taskCode);

            if (!dbIds || dbIds.length === 0) return;

            if (schedule.endDate <= date) {
                // Task completed - show elements
                visibleDbIds.push(...dbIds);
            } else {
                // Task not completed - hide elements
                hiddenDbIds.push(...dbIds);
            }
        });

        // Apply visibility
        if (hiddenDbIds.length > 0) {
            this.forgeViewer.hideDbIds(hiddenDbIds);
        }

        if (visibleDbIds.length > 0) {
            this.forgeViewer.showDbIds(visibleDbIds);
        }

        // Refresh scene
        this.forgeViewer.refreshScene();
    }
}
